import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Calendar, Users, Sparkles, ArrowRight, Sun, Map, Clock, Plus, Minus, Tag, Mail, MessageCircle, CheckCircle, PhoneCall, X, ListChecks, PlaneTakeoff, Loader2, Activity, CheckCircle2, Bot, Trash2, ShoppingBag } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

// 1. Import our new Invincible Memory Hook
import { useMemory } from '../hooks/useMemory';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext'; // 👈 Theme Support Added

// Import from master data
import { itineraries as importedItineraries } from './ItinerariesPage'; 
import { experiences as importedExperiences } from './ExperiencesPage'; 

const AGENCY_WHATSAPP_NUMBER = "919353520020"; 
const AGENCY_EMAIL_ADDRESS = "hello@odessey.in"; 

// --- NEW HELPER: Fetch coordinates using FastAPI Backend ---
const fetchCoordinates = async (address) => {
  if (!address) return null;
  try {
    const response = await fetch('/api/geocode', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query: address })
    });
    
    if (!response.ok) {
      console.error("Geocoding Error:", response.statusText);
      return null;
    }
    
    const data = await response.json();
    if (data?.results?.[0]) {
      const result = data.results[0];
      return { lat: result.lat, lng: result.lng };
    }
  } catch (error) {
    console.error("Backend Geocoding Error:", error);
  }
  return null;
};

// --- UPDATED: Robust fetchWikiImage from SearchResults ---
const fetchWikiImage = async (title) => {
  if (!title) return null;
  try {
    const normalizeUrl = (src) => {
      if (!src) return null;
      if (src.startsWith('//')) return 'https:' + src;
      return src;
    };

    const pageImageForTitle = async (t, endpoint = 'https://en.wikipedia.org/w/api.php') => {
      const params = `origin=*&action=query&format=json&prop=pageimages&pithumbsize=800&titles=${encodeURIComponent(t)}`;
      const res = await fetch(`${endpoint}?${params}`).then(r => r.json()).catch(() => ({}));
      const pages = res.query?.pages;
      const pageId = pages ? Object.keys(pages)[0] : null;
      if (pageId && pageId !== '-1') {
        const page = pages[pageId];
        if (page.thumbnail?.source) return normalizeUrl(page.thumbnail.source);
      }
      return null;
    };

    let img = await pageImageForTitle(title, 'https://en.wikipedia.org/w/api.php');
    if (img) return img;

    img = await pageImageForTitle(title, 'https://en.wikivoyage.org/w/api.php');
    if (img) return img;

    const searchEndpoints = [
      'https://en.wikipedia.org/w/api.php',
      'https://en.wikivoyage.org/w/api.php'
    ];

    for (const endpoint of searchEndpoints) {
      try {
        const sParams = `origin=*&action=query&list=search&srsearch=${encodeURIComponent(title + ' India')}&srlimit=5&format=json`;
        const sRes = await fetch(`${endpoint}?${sParams}`).then(r => r.json()).catch(() => ({}));
        const hits = sRes.query?.search || [];
        if (hits.length > 0) {
          for (let i = 0; i < Math.min(hits.length, 3); i++) {
            const candidate = hits[i].title;
            const candImg = await pageImageForTitle(candidate, endpoint);
            if (candImg) return candImg;
          }
        }
      } catch (e) {}
    }

    try {
      const sParams = `origin=*&action=query&list=search&srsearch=${encodeURIComponent(title)}&srlimit=5&format=json`;
      const sRes = await fetch(`https://en.wikipedia.org/w/api.php?${sParams}`).then(r => r.json()).catch(() => ({}));
      const hits = sRes.query?.search || [];
      for (let i = 0; i < Math.min(hits.length, 3); i++) {
        const candImg = await pageImageForTitle(hits[i].title, 'https://en.wikipedia.org/w/api.php');
        if (candImg) return candImg;
      }
    } catch (e) {}

    return null;
  } catch (e) {
    return null;
  }
};

const PlanTrip = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDark } = useTheme(); // 👈 Access Theme State

  // 2. CONNECT MEMORY: These variables will now survive EVERYTHING
  const [formData, setFormData] = useMemory('odessey_pt_form', {
    origin: '', destination: '', startDate: '', endDate: '', groupType: '', pax: 1, children: 0, interests: ''
  });
  const [routeStops, setRouteStops] = useMemory('odessey_pt_routes', []);
    
  // CHANGED: Instead of a single object, we now track an array of selected plans/items
  const [selectedPlans, setSelectedPlans] = useMemory('odessey_pt_plans', []);

  // --- STANDARD UI STATES ---
  const [cartItems, setCartItems] = useState([]);
  const [originSuggestions, setOriginSuggestions] = useState([]);
  const [destSuggestions, setDestSuggestions] = useState([]);
  const [activeDropdown, setActiveDropdown] = useState(null); 
  const [isLoadingPlaces, setIsLoadingPlaces] = useState({ origin: false, dest: false });
  const originTimer = useRef(null);
  const destTimer = useRef(null);
 
  const [previewItin, setPreviewItin] = useState(null); 
  const [previewExp, setPreviewExp] = useState(null); 
  const [modalState, setModalState] = useState('none'); 
  const [contactMethod, setContactMethod] = useState('whatsapp'); 
  const [contactValue, setContactValue] = useState('');

  const [aiGeneratedItinerary, setAiGeneratedItinerary] = useState(null);
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [currentCalcDuration, setCurrentCalcDuration] = useState("4 Days / 3 Nights"); 

  const masterItineraries = importedItineraries || [];
  const masterExperiences = importedExperiences || [];

  // --- LOAD CART ON MOUNT ---
  useEffect(() => {
    const savedCart = JSON.parse(localStorage.getItem('odessey_cart') || '[]');
    setCartItems(savedCart);
  }, []);

  const removeFromCart = (id) => {
    const updated = cartItems.filter(item => item.id !== id);
    setCartItems(updated);
    localStorage.setItem('odessey_cart', JSON.stringify(updated));
  };
  
  const handleRemovePlan = (titleToRemove) => {
    setSelectedPlans(prev => prev.filter(p => p.title !== titleToRemove));
  };
  
  // 3. GHOST WIPE: Process incoming data safely, then permanently disarm it
  useEffect(() => {
    if (location.state && !location.state.isConsumed) {
      const stateData = location.state;
      let incomingDest = '';
      let newPlan = null;

      if (stateData.prefillPackage) {
        const pkg = stateData.prefillPackage;
        const fullDetails = masterItineraries.find(i => i.title === pkg.title) || pkg;
        newPlan = { ...fullDetails, itemType: 'Template' };
        incomingDest = fullDetails.dest || fullDetails.location;
      } else if (stateData.prefillLodge) {
        newPlan = { ...stateData.prefillLodge, itemType: 'Lodge' };
        incomingDest = stateData.prefillLodge.location;
      } else if (stateData.prefillExperience) {
        const exp = stateData.prefillExperience;
        newPlan = { title: exp.title, dest: exp.location, days: exp.duration, description: exp.fullDesc, highlights: exp.inclusions?.join(', ') || exp.desc, itemType: 'Experience' };
        incomingDest = exp.location.split(',')[0];
            } 

      if (newPlan) {
        setSelectedPlans(prev => prev.some(p => p.title === newPlan.title) ? prev : [...prev, newPlan]);
      }

      if (stateData.routeStops) {
        setRouteStops(stateData.routeStops);
      }

      setFormData(prev => {
        let mergedInterests = prev.interests;
        const incomingInterests = stateData.interests || '';
        if (incomingInterests && (!prev.interests || !prev.interests.includes(incomingInterests))) {
          mergedInterests = prev.interests ? prev.interests + "\n\n" + incomingInterests : incomingInterests;
        }

        return {
          ...prev,
          origin: stateData.origin || stateData.from || prev.origin,
          destination: stateData.presetDestination || incomingDest || stateData.destination || stateData.to || prev.destination,
          startDate: stateData.startDate || stateData.date || prev.startDate,
          endDate: stateData.endDate || prev.endDate,
          pax: stateData.passengers || stateData.pax || prev.pax,
          children: stateData.children || prev.children,
          interests: mergedInterests,
        };
      });

      navigate(location.pathname + location.search, { replace: true, state: { ...location.state, isConsumed: true } });
    }
  }, [location, navigate, masterItineraries]);

  const searchTarget = formData.destination.toLowerCase().trim();
  
  const filteredItineraries = searchTarget
    ? masterItineraries.filter(itin => 
        (itin.dest && itin.dest.toLowerCase().includes(searchTarget)) || 
        (itin.location && itin.location.toLowerCase().includes(searchTarget)) || 
        (itin.title && itin.title.toLowerCase().includes(searchTarget))
      )
    : masterItineraries;

  const filteredExperiences = searchTarget
    ? masterExperiences.filter(exp => 
        (exp.location && exp.location.toLowerCase().includes(searchTarget)) || 
        (exp.title && exp.title.toLowerCase().includes(searchTarget))
      )
    : masterExperiences;

  // --- SMART AI GENERATION USING FASTAPI BACKEND ROUTING ---
  useEffect(() => {
    if (searchTarget && filteredItineraries.length === 0) {
      setAiGeneratedItinerary(null);
      setIsGeneratingAI(true);

      let numDays = 4;
      let durationString = "4 Days / 3 Nights";
      
      if (formData.startDate && formData.endDate) {
        const start = new Date(formData.startDate);
        const end = new Date(formData.endDate);
        start.setHours(0, 0, 0, 0);
        end.setHours(0, 0, 0, 0);
        
        const diffTime = end.getTime() - start.getTime();
        if (diffTime >= 0) {
          numDays = Math.round(diffTime / (1000 * 60 * 60 * 24)) + 1;
          const nights = Math.max(0, numDays - 1);
          durationString = `${numDays} Days / ${nights} Nights`;
        }
      }
      
      setCurrentCalcDuration(durationString); 

      const timer = setTimeout(async () => {
        try {
          const planTemplate = Array.from({ length: numDays }, (_, i) => 
            `{ "day": ${i + 1}, "title": "Activity or Checkpoint for Day ${i + 1}", "desc": "Detailed explanation of activities, travel checkpoints, or local sightseeing for Day ${i + 1}" }`
          ).join(",\n            ");

          const manualStopsSummary = routeStops.length > 0 
            ? `The user explicitly requested to stop at these locations: ${routeStops.map(s => s.name).join(', ')}.`
            : '';

          let routeContext = "";
          
          if (formData.origin && formData.destination) {
            try {
              const routeResponse = await fetch('/api/get_route', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                  origin: formData.origin, 
                  destination: formData.destination 
                })
              });

              if (routeResponse.ok) {
                const routeData = await routeResponse.json();
                
                const distanceMeters = routeData?.distance;
                const durationSeconds = routeData?.duration;

                if (distanceMeters && durationSeconds) {
                  const distKm = Math.round(distanceMeters / 1000);
                  const durationHours = Math.round(durationSeconds / 3600);
                  
                  routeContext = `\n\nROUTE DATA FROM OLA MAPS:\n- Driving Distance: ${distKm} km\n- Estimated Driving Time: ${durationHours} hours\n`;
                  
                  if (durationHours > 12) {
                    routeContext += `\nCRITICAL INSTRUCTION: The driving time exceeds 12 hours. DO NOT plan a road trip. Explicitly suggest taking a flight or fast train from ${formData.origin} to ${formData.destination} on Day 1. Focus the ENTIRE rest of the itinerary purely on local sightseeing and experiences IN AND AROUND ${formData.destination}. Do NOT suggest driving stops halfway across the country.`;
                  } else {
                    routeContext += `\nCRITICAL INSTRUCTION: This is a manageable driving distance. Design this as a scenic road trip. Suggest interesting landmarks, cafes, or towns to stop at along the route between ${formData.origin} and ${formData.destination}.`;
                  }
                }
              }
            } catch (err) {
              console.error("Routing Error:", err);
            }
          }
          
          if (!routeContext) {
             routeContext = `\n\nCRITICAL INSTRUCTION: Evaluate the geographic distance between ${formData.origin || 'the origin'} and ${formData.destination}. If they are far apart (e.g. > 12 hours driving), explicitly suggest a flight/train for Day 1 and focus the itinerary LOCALLY at ${formData.destination}. If they are close, make it a road trip with stops along the way.`;
          }

          const prompt = `You are an expert Indian travel planner and route coordinator. The user wants a journey from "${formData.origin || 'their location'}" to "${formData.destination}" lasting EXACTLY ${numDays} days (${durationString}).
          
          ${routeContext}
          ${manualStopsSummary}
          
          Return ONLY a valid JSON object. Do not include formatting or markdown wrappers. The structure MUST match this format exactly:
          {
            "title": "A catchy trip title",
            "dest": "${formData.destination}",
            "days": "${durationString}",
            "description": "A compelling 2-sentence overview.",
            "highlights": ["Highlight 1", "Highlight 2", "Highlight 3"],
            "plan": [
              ${planTemplate}
            ]
          }`;

          const response = await fetch('/api/chat', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              messages: [{ role: 'user', content: prompt }],
              max_tokens: 4096
            })
          });

          if (!response.ok) {
            const error = await response.text();
            throw new Error(`Chat API error: ${response.status} ${error}`);
          }

          const data = await response.json();
          const rawContent = data.reply;

          const startIndex = rawContent.indexOf('{');
          const endIndex = rawContent.lastIndexOf('}');
          const cleanJSON = rawContent.substring(startIndex, endIndex + 1);

          const parsedItinerary = JSON.parse(cleanJSON);
          parsedItinerary.isAiGenerated = true;
          setAiGeneratedItinerary(parsedItinerary);
        } catch (error) {
          console.error("Route Generation Failed:", error);
        } finally {
          setIsGeneratingAI(false);
        }
      }, 1500);

      return () => clearTimeout(timer);
    } else {
      setIsGeneratingAI(false);
      setAiGeneratedItinerary(null);
    }
  }, [searchTarget, filteredItineraries.length, formData.origin, formData.destination, formData.startDate, formData.endDate, routeStops]);

  // --- DYNAMIC AI SEASONAL SUGGESTIONS ---
  const [seasonalPicks, setSeasonalPicks] = useState([]);
  const [isLoadingSeasonal, setIsLoadingSeasonal] = useState(true);
  
  const referenceDate = formData.startDate ? new Date(formData.startDate) : new Date();
  const safeDate = !isNaN(referenceDate.getTime()) ? referenceDate : new Date();
  const currentMonthNameDisplay = safeDate.toLocaleString('default', { month: 'long' });

  useEffect(() => {
    const fetchSeasonalPicks = async () => {
      setIsLoadingSeasonal(true);
      try {
        const prompt = `You are an expert Indian travel planner. Suggest exactly 4 destinations in India that are absolutely perfect to visit in the month of ${currentMonthNameDisplay} due to weather, landscapes, or festivals.
        Return ONLY a valid JSON array of exactly 4 objects. Do not include formatting.
        Structure MUST match exactly:
        [
          {
            "name": "Destination Name",
            "desc": "A compelling 1-sentence description of why it's great in ${currentMonthNameDisplay}."
          }
        ]`;

        const response = await fetch('/api/chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            messages: [{ role: 'user', content: prompt }],
            max_tokens: 2048
          })
        });

        if (!response.ok) {
          const error = await response.text();
          throw new Error(`Chat API error: ${response.status} ${error}`);
        }

        const data = await response.json();
        const rawContent = data.reply;
        const startIndex = rawContent.indexOf('[');
        const endIndex = rawContent.lastIndexOf(']');
        const cleanJSON = rawContent.substring(startIndex, endIndex + 1);
        
        const aiPicks = JSON.parse(cleanJSON);

        const withImages = await Promise.all(aiPicks.map(async (pick) => {
          let img = await fetchWikiImage(pick.name);
          return {
            ...pick,
            img: img || '/logo.png'
          };
        }));

        setSeasonalPicks(withImages);
      } catch (error) {
        console.error("Seasonal Picks Generation failed:", error);
        setSeasonalPicks([
          { name: "Manali", desc: "Snow-capped mountains with perfect seasonal relief.", img: "https://s7ap1.scene7.com/is/image/incredibleindia/rohtang-pass-manali-himachal-pradesh-1-attr-hero?qlt=82&ts=1726730701545" },
          { name: "Goa", desc: "Vibrant coastal energy and stunning sunsets.", img: "https://s7ap1.scene7.com/is/image/incredibleindia/cola-beach-goa-blog-ntr-hero?qlt=82&ts=1742160482227" },
          { name: "Munnar", desc: "Lush tea plantations and misty mornings.", img: "https://www.keralatourpackage.com/assets/images/destinations/munnar_horizontal.webp" },
          { name: "Udaipur", desc: "Romantic lakes and royal heritage.", img: "https://images.contentstack.io/v3/assets/blt06f605a34f1194ff/blt4cf134d7dc5ff2c0/68906f5f7adc79b45ae98a6f/iStock-2197451116-2-HEADER_MOBILE.jpg?format=webp&auto=avif&quality=60&crop=1%3A1&width=1440" }
        ]);
      } finally {
        setIsLoadingSeasonal(false);
      }
    };

    fetchSeasonalPicks();
  }, [currentMonthNameDisplay]);

  const fetchPlaces = async (input, type) => {
    if (!input || input.length < 2) {
      type === 'origin' ? setOriginSuggestions([]) : setDestSuggestions([]);
      return;
    }
    setIsLoadingPlaces(prev => ({ ...prev, [type]: true }));

    try {
      const response = await fetch('/api/get_ola_search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: input })
      });

      if (response.ok) {
        const data = await response.json();
        if (data?.results && Array.isArray(data.results)) {
          const results = data.results.map(r => r.formatted_address || r.name);
          type === 'origin' ? setOriginSuggestions(results) : setDestSuggestions(results);
        }
      } else {
        const offlineCities = ["Ahmedabad, Gujarat", "Agra, Uttar Pradesh", "Amritsar, Punjab", "Bangalore, Karnataka", "Bhopal, Madhya Pradesh", "Chennai, Tamil Nadu", "Chandigarh", "Coimbatore, Tamil Nadu", "Dehradun, Uttarakhand", "Delhi", "Goa", "Guwahati, Assam", "Hyderabad, Telangana", "Indore, Madhya Pradesh", "Jaipur, Rajasthan", "Jodhpur, Rajasthan", "Kochi, Kerala", "Kolkata, West Bengal", "Lucknow, Uttar Pradesh", "Madurai, Tamil Nadu", "Manali, Himachal Pradesh", "Mumbai, Maharashtra", "Mysore, Karnataka", "Nagpur, Maharashtra", "New Delhi", "Pune, Maharashtra", "Rishikesh, Uttarakhand", "Shimla, Himachal Pradesh", "Surat, Gujarat", "Thiruvananthapuram, Kerala", "Udaipur, Rajasthan", "Varanasi, Uttar Pradesh", "Visakhapatnam, Andhra Pradesh"];
        const filtered = offlineCities.filter(c => c.toLowerCase().includes(input.toLowerCase())).slice(0, 5);
        type === 'origin' ? setOriginSuggestions(filtered) : setDestSuggestions(filtered);
      }
    } catch (error) { 
      const offlineCities = ["Ahmedabad, Gujarat", "Agra, Uttar Pradesh", "Amritsar, Punjab", "Bangalore, Karnataka", "Bhopal, Madhya Pradesh", "Chennai, Tamil Nadu", "Chandigarh", "Coimbatore, Tamil Nadu", "Dehradun, Uttarakhand", "Delhi", "Goa", "Guwahati, Assam", "Hyderabad, Telangana", "Indore, Madhya Pradesh", "Jaipur, Rajasthan", "Jodhpur, Rajasthan", "Kochi, Kerala", "Kolkata, West Bengal", "Lucknow, Uttar Pradesh", "Madurai, Tamil Nadu", "Manali, Himachal Pradesh", "Mumbai, Maharashtra", "Mysore, Karnataka", "Nagpur, Maharashtra", "New Delhi", "Pune, Maharashtra", "Rishikesh, Uttarakhand", "Shimla, Himachal Pradesh", "Surat, Gujarat", "Thiruvananthapuram, Kerala", "Udaipur, Rajasthan", "Varanasi, Uttar Pradesh", "Visakhapatnam, Andhra Pradesh"];
      const filtered = offlineCities.filter(c => c.toLowerCase().includes(input.toLowerCase())).slice(0, 5);
      type === 'origin' ? setOriginSuggestions(filtered) : setDestSuggestions(filtered);
    }
    
    setIsLoadingPlaces(prev => ({ ...prev, [type]: false }));
  };

  const handlePlaceInputChange = (e, type) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, [type]: value }));
    setActiveDropdown(type);

    const timer = type === 'origin' ? originTimer : destTimer;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => fetchPlaces(value, type === 'origin' ? 'origin' : 'dest'), 300);
  };

  const selectPlace = (place, type) => {
    setFormData(prev => ({ ...prev, [type]: place }));
    setActiveDropdown(null);
    type === 'origin' ? setOriginSuggestions([]) : setDestSuggestions([]);
  };

  const handleTagClick = (tag) => {
    setFormData(prev => {
      let tagsArray = prev.interests.split(',').map(t => t.trim()).filter(t => t);
      if (tagsArray.includes(tag)) {
        tagsArray = tagsArray.filter(t => t !== tag);
      } else {
        tagsArray.push(tag);
      }
      return { ...prev, interests: tagsArray.join(', ') };
    });
  };

  const handleStartPlanning = (e) => {
    e.preventDefault();
    setModalState('contact');
  };

  const handleTemplateConfirm = (itin) => {
    setSelectedPlans(prev => prev.some(p => p.title === itin.title) ? prev : [...prev, { ...itin, itemType: 'Template' }]);
    setFormData(prev => ({ ...prev, destination: prev.destination || itin.dest || itin.location || '' }));
    setModalState('none');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleExperienceConfirm = (exp) => {
    const newExp = { title: exp.title, dest: exp.location, days: exp.duration, description: exp.fullDesc, highlights: exp.inclusions?.join(', ') || exp.desc, itemType: 'Experience' };
    setSelectedPlans(prev => prev.some(p => p.title === newExp.title) ? prev : [...prev, newExp]);
    setFormData(prev => ({ ...prev, destination: prev.destination || exp.location.split(',')[0] }));
    setModalState('none');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const closeModal = () => {
    setModalState('none');
    setPreviewItin(null);
    setPreviewExp(null);
  };

  const handleGlobalClick = () => {
    if (activeDropdown) setActiveDropdown(null);
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault(); 
    
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    
    const planTitles = selectedPlans.map(p => p.title).join(' + ');
    const firstPlan = selectedPlans[0] || {};
    
    const planToSave = selectedPlans.length > 0 ? {
      title: (`Custom Trip: ${planTitles}`).substring(0, 100),
      dest: formData.destination || firstPlan.dest || firstPlan.location || '',
      days: selectedPlans.find(p => p.days && p.days !== 'Flexible')?.days || currentCalcDuration || "Flexible",
      description: selectedPlans.map(p => p.description).join(' | '),
      highlights: selectedPlans.flatMap(p => Array.isArray(p.highlights) ? p.highlights : (p.highlights ? p.highlights.split(', ') : [])),
      plan: selectedPlans.flatMap(p => p.plan || []), 
      isAiGenerated: selectedPlans.some(p => p.isAiGenerated)
    } : (aiGeneratedItinerary || {
      title: `Custom Trip to ${formData.destination}`,
      dest: formData.destination,
      days: currentCalcDuration || "Flexible",
      description: formData.interests || "Custom Journey planned via Odessey",
      highlights: [],
      plan: [],
      isAiGenerated: false
    });

    const backendPayload = {
      title: planToSave.title,
      dest: planToSave.dest,
      days: planToSave.days,
      description: planToSave.description,
      highlights: planToSave.highlights,
      plan: planToSave.plan.map((p, index) => ({  
        day: parseInt(p.day) || (index + 1), 
        title: p.title || `Day ${index + 1}`, 
        desc: p.desc || p.description || '' 
      })),
      isAiGenerated: !!planToSave.isAiGenerated,
      origin: formData.origin,
      startDate: formData.startDate,
      endDate: formData.endDate,
      groupType: formData.groupType,
      pax: parseInt(formData.pax) || 1,
      children: parseInt(formData.children) || 0,
      interests: formData.interests,
    };

    try {
      if (token) {
        const response = await fetch('/api/save_itinerary', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(backendPayload)
        });

        if (!response.ok) {
          console.error("Failed to save itinerary to backend");
        }
      }
    } catch (error) {
      console.error("Backend Error saving itinerary:", error);
    }

    const cartSummary = cartItems.length > 0 
      ? `\n\n*Selected Experiences from Cart:* \n${cartItems.map((item, i) => `${i+1}. ${item.title} (${item.location})`).join('\n')}`
      : '';

    const routeSummary = routeStops.length > 0 
      ? `\n\n*Custom Route Stops:* \n${routeStops.map((stop, i) => `${i+1}. ${stop.name}`).join('\n')}`
      : '';

    const selectedSummary = selectedPlans.length > 0
      ? selectedPlans.map(p => p.title).join(', ')
      : 'Custom Trip';

    const messageDetails = `*New Trip Inquiry!* ✈️
*From:* ${formData.origin}
*To:* ${formData.destination}
*Dates:* ${formData.startDate} to ${formData.endDate}
*Travellers:* ${formData.pax} Adults, ${formData.children} Children
*Interests:* ${formData.interests || 'None'}
*Target Plan:* ${selectedSummary}${routeSummary}${cartSummary}
*Customer Info:* ${contactValue}`;

    if (user && user.email) {
      const tripData = {
        trip_name: `${formData.destination || 'Custom Trip'} (${formData.startDate} to ${formData.endDate})`,
        primary_user: {
          name: user.name || contactValue.split('@')[0] || 'Guest',
          email: user.email,
          phone: user.mobile_number || (contactMethod === 'whatsapp' ? contactValue : '') || '',
          emergencyContactName: 'Not provided',
          emergencyContactRelation: 'Not provided',
          emergencyContactPhone: '',
          dietary: 'Not specified',
          medicalInfo: ''
        },
        additional_guests: [],
        origin: formData.origin || null,
        destination: formData.destination || null,
        start_date: formData.startDate || null,
        end_date: formData.endDate || null,
        pax: parseInt(formData.pax) || 1,
        children: parseInt(formData.children) || 0,
        interests: formData.interests || null,
        message_details: messageDetails,
      };

      fetch('/api/send_trip_details', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(tripData)
      })
      .catch(err => {
        console.error('❌ Error sending trip email:', err.message);
      });
    }

    if (contactMethod === 'whatsapp') {
      const waLink = `https://wa.me/${AGENCY_WHATSAPP_NUMBER}?text=${encodeURIComponent(messageDetails)}`;
      window.open(waLink, '_blank');
    }

    setModalState('success'); 
  };

  const handleClearAll = () => {
    setFormData({
      origin: '', destination: '', startDate: '', endDate: '', groupType: '', pax: 1, children: 0, interests: ''
    });
    setRouteStops([]);
    setSelectedPlans([]);

    cartItems.forEach(item => removeFromCart(item.id));
    localStorage.removeItem('odessey_cart');

    setAiGeneratedItinerary(null);
    setIsGeneratingAI(false);
    setOriginSuggestions([]);
    setDestSuggestions([]);

    navigate(location.pathname + location.search, { replace: true, state: {} });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: isDark ? '#111827' : '#f9fafb', paddingBottom: '80px', fontFamily: 'system-ui, -apple-system, sans-serif', transition: 'background-color 0.3s', boxSizing: 'border-box' }} onClick={handleGlobalClick}>
      
      {/* 1. HERO */}
      <div style={{ position: 'relative', height: '40vh', minHeight: '320px', backgroundImage: 'url(https://images.unsplash.com/photo-1506461883276-594a12b11cf3?auto=format&fit=crop&w=1920&q=80)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(17, 24, 39, 0.5), rgba(17, 24, 39, 0.85))' }} />
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', width: '100%', padding: '0 16px', boxSizing: 'border-box' }}>
          <h1 style={{ fontSize: 'clamp(32px, 5vw, 42px)', fontWeight: '800', color: 'white', margin: 0 }}>Refine Your Journey</h1>
        </div>
      </div>

      {/* 2. FORM AREA */}
      <div style={{ maxWidth: '1000px', margin: '-40px auto 40px', padding: '0 16px', position: 'relative', zIndex: 10, boxSizing: 'border-box' }}>
        
        <div style={{ backgroundColor: isDark ? '#1f2937' : 'white', borderRadius: '24px', padding: '30px 20px', boxShadow: isDark ? '0 20px 40px rgba(0,0,0,0.5)' : '0 20px 40px rgba(0,0,0,0.1)', position: 'relative', border: isDark ? '1px solid #374151' : 'none', transition: 'background-color 0.3s', boxSizing: 'border-box' }}>
          
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px', marginTop: '-10px' }}>
            <button 
              type="button" 
              onClick={handleClearAll} 
              style={{ background: 'transparent', border: 'none', color: isDark ? '#9ca3af' : '#94a3b8', fontSize: '13px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', transition: 'all 0.2s' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.transform = 'scale(1.05)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = isDark ? '#9ca3af' : '#94a3b8'; e.currentTarget.style.transform = 'scale(1)'; }}
            >
              <Trash2 size={15} /> Clear Form
            </button>
          </div>

          {/* --- ROUTE TIMELINE PREVIEW --- */}
          {routeStops.length > 0 && formData.origin && formData.destination && (
            <div style={{ backgroundColor: isDark ? '#111827' : '#f8fafc', border: isDark ? '1px solid #374151' : '1px solid #e2e8f0', borderRadius: '16px', padding: '20px', marginBottom: '25px', animation: 'slideDown 0.4s ease-out', boxSizing: 'border-box' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#1e293b', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Map color="#0284c7" size={18} /> Your Custom Route
                </h3>
                <button 
                  onClick={() => setRouteStops([])} 
                  style={{ background: isDark ? '#374151' : '#f1f5f9', border: 'none', padding: '4px 10px', borderRadius: '50px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer', color: '#ef4444' }}
                >
                  Clear Route
                </button>
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', position: 'relative', paddingLeft: '15px', borderLeft: isDark ? '2px dashed #4b5563' : '2px dashed #cbd5e1', marginLeft: '10px' }}>
                <div style={{ position: 'relative' }}>
                   <div style={{ position: 'absolute', left: '-22px', top: '4px', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: isDark ? '#f9fafb' : '#111827', border: '2px solid white' }} />
                   <h4 style={{ fontSize: '14px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#111827', margin: 0 }}>{formData.origin}</h4>
                   <p style={{ fontSize: '11px', color: isDark ? '#9ca3af' : '#64748b', margin: 0 }}>Starting Point</p>
                </div>

                {routeStops.map((stop, idx) => (
                   <div key={idx} style={{ position: 'relative' }}>
                      <div style={{ position: 'absolute', left: '-22px', top: '2px', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#16a34a', border: '2px solid white' }} />
                      <h4 style={{ fontSize: '13px', fontWeight: '600', color: isDark ? '#d1d5db' : '#334155', margin: 0 }}>{stop.name}</h4>
                   </div>
                ))}

                <div style={{ position: 'relative' }}>
                   <div style={{ position: 'absolute', left: '-22px', top: '4px', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#ea580c', border: '2px solid white' }} />
                   <h4 style={{ fontSize: '14px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#111827', margin: 0 }}>{formData.destination}</h4>
                   <p style={{ fontSize: '11px', color: isDark ? '#9ca3af' : '#64748b', margin: 0 }}>Final Destination</p>
                </div>
              </div>
            </div>
          )}

          {/* --- DATA PREVIEW CARDS (STACKED) --- */}
          {(selectedPlans.length > 0 || cartItems.length > 0) && (
            <div style={{ backgroundColor: isDark ? '#111827' : '#f8fafc', border: isDark ? '1px solid #374151' : '1px solid #e2e8f0', borderRadius: '16px', padding: '20px', marginBottom: '25px', animation: 'slideDown 0.4s ease-out', boxSizing: 'border-box' }}>
              
              {selectedPlans.length > 0 && (
                <div style={{ marginBottom: cartItems.length > 0 ? '20px' : '0', paddingBottom: cartItems.length > 0 ? '20px' : '0', borderBottom: cartItems.length > 0 ? (isDark ? '1px dashed #374151' : '1px dashed #cbd5e1') : 'none' }}>
                  <h3 style={{ fontSize: '15px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#1e293b', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Sparkles size={16} color="#0284c7" /> Your Active Selections ({selectedPlans.length})
                  </h3>
                  
                  {selectedPlans.map((plan, idx) => (
                    <div key={idx} style={{ position: 'relative', marginBottom: idx === selectedPlans.length - 1 ? '0' : '20px', paddingBottom: idx === selectedPlans.length - 1 ? '0' : '20px', borderBottom: idx === selectedPlans.length - 1 ? 'none' : (isDark ? '1px solid #374151' : '1px solid #e2e8f0') }}>
                      
                      <button onClick={() => handleRemovePlan(plan.title)} title="Remove Selection" style={{ position: 'absolute', top: '0', right: '0', background: isDark ? '#450a0a' : '#fee2e2', border: 'none', color: '#ef4444', width: '28px', height: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'transform 0.2s' }} onMouseEnter={(e)=>e.currentTarget.style.transform='scale(1.1)'} onMouseLeave={(e)=>e.currentTarget.style.transform='scale(1)'}>
                        <X size={13} />
                      </button>

                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', paddingRight: '35px' }}>
                        <div style={{ backgroundColor: plan.itemType === 'Experience' ? (isDark ? '#064e3b' : '#f0fdf4') : (isDark ? '#082f49' : '#e0f2fe'), color: plan.itemType === 'Experience' ? '#16a34a' : '#0284c7', padding: '8px', borderRadius: '10px', flexShrink: 0 }}>
                          {plan.itemType === 'Experience' ? <Activity size={18}/> : <ListChecks size={18}/>}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#1e293b', margin: '0 0 4px 0', lineHeight: '1.3' }}>
                            {plan.title || plan.name}
                            {plan.isAiGenerated && <span style={{ marginLeft: '8px', fontSize: '10px', backgroundColor: isDark ? '#374151' : '#f1f5f9', color: isDark ? '#d1d5db' : '#475569', padding: '2px 6px', borderRadius: '50px', verticalAlign: 'middle' }}>✨ Route Planner</span>}
                          </h3>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', fontSize: '12px', color: isDark ? '#9ca3af' : '#64748b', fontWeight: '600', marginBottom: '8px' }}>
                            <span><Clock size={13} style={{display:'inline', marginRight:'3px', verticalAlign:'text-bottom'}}/> {plan.days || 'Flexible'}</span>
                            <span><MapPin size={13} style={{display:'inline', marginRight:'3px', verticalAlign:'text-bottom'}}/> {plan.dest || plan.location}</span>
                          </div>
                          <p style={{ margin: 0, fontSize: '13px', color: isDark ? '#9ca3af' : '#475569', lineHeight: '1.5' }}>
                            {Array.isArray(plan.highlights) 
                              ? plan.highlights.join(', ') 
                              : (plan.highlights || plan.description || "Customizable travel features added.")}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {cartItems.length > 0 && (
                <div>
                  <h3 style={{ fontSize: '15px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#1e293b', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <ShoppingBag size={16} color="#16a34a" /> Included from your Trip Cart ({cartItems.length})
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' }}>
                    {cartItems.map((item) => (
                      <div key={item.id} style={{ display: 'flex', gap: '10px', backgroundColor: isDark ? '#1f2937' : 'white', padding: '10px', borderRadius: '10px', border: isDark ? '1px solid #374151' : '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)', position: 'relative', boxSizing: 'border-box' }}>
                        <img src={item.image} alt={item.title} style={{ width: '40px', height: '40px', borderRadius: '6px', objectFit: 'cover', flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                          <h4 style={{ fontSize: '12px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', margin: '0 0 2px 0' }}>{item.title}</h4>
                          <p style={{ fontSize: '10px', color: isDark ? '#9ca3af' : '#94a3b8', margin: 0 }}><MapPin size={9} style={{display:'inline'}}/> {item.location}</p>
                        </div>
                        <button onClick={() => removeFromCart(item.id)} title="Remove" style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', padding: '4px', flexShrink: 0 }}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </div>
          )}

          <form onSubmit={handleStartPlanning}>
            <div className="form-grid">
              
              {/* Origin Autocomplete */}
              <div className="col-span-2" style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: isDark ? '#9ca3af' : '#64748b', marginBottom: '6px', textTransform: 'uppercase' }}>Origin</label>
                <div style={{ position: 'relative' }}>
                  <PlaneTakeoff size={16} color="#94a3b8" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                  <input 
                    type="text" required value={formData.origin} onChange={(e) => handlePlaceInputChange(e, 'origin')} 
                    onFocus={() => setActiveDropdown('origin')} placeholder="Starting city" 
                    style={{ width: '100%', boxSizing:'border-box', height: '44px', padding: '0 12px 0 38px', borderRadius: '10px', border: isDark ? '1px solid #4b5563' : '1px solid #e2e8f0', outline: 'none', color: isDark ? 'white' : '#334151', fontSize: '13px', backgroundColor: isDark ? '#111827' : 'white' }} 
                  />
                  {isLoadingPlaces.origin && <Loader2 size={15} color="#94a3b8" className="animate-spin" style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }} />}
                </div>
                {activeDropdown === 'origin' && originSuggestions.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '6px', backgroundColor: isDark ? '#1f2937' : 'white', borderRadius: '10px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)', border: isDark ? '1px solid #374151' : '1px solid #e2e8f0', zIndex: 50, overflow: 'hidden' }}>
                    {originSuggestions.map((place, i) => (
                      <div key={i} onClick={() => selectPlace(place, 'origin')} style={{ padding: '10px 14px', fontSize: '13px', color: isDark ? '#d1d5db' : '#334155', cursor: 'pointer', borderBottom: i === originSuggestions.length -1 ? 'none' : (isDark ? '1px solid #374151' : '1px solid #f1f5f9') }} onMouseEnter={(e)=>e.currentTarget.style.backgroundColor=isDark ? '#374151' : '#f8fafc'} onMouseLeave={(e)=>e.currentTarget.style.backgroundColor='transparent'}>
                        <MapPin size={13} color="#94a3b8" style={{ display: 'inline', marginRight: '6px' }} /> {place}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Destination Autocomplete */}
              <div className="col-span-2" style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: isDark ? '#9ca3af' : '#64748b', marginBottom: '6px', textTransform: 'uppercase' }}>Destination</label>
                <div style={{ position: 'relative' }}>
                  <MapPin size={16} color="#94a3b8" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                  <input 
                    type="text" required value={formData.destination} onChange={(e) => handlePlaceInputChange(e, 'destination')} 
                    onFocus={() => setActiveDropdown('destination')} placeholder="Going to" 
                    style={{ width: '100%', boxSizing:'border-box', height: '44px', padding: '0 12px 0 38px', borderRadius: '10px', border: isDark ? '1px solid #4b5563' : '1px solid #e2e8f0', outline: 'none', color: isDark ? 'white' : '#334151', fontSize: '13px', backgroundColor: isDark ? '#111827' : 'white' }} 
                  />
                  {isLoadingPlaces.dest && <Loader2 size={15} color="#94a3b8" className="animate-spin" style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }} />}
                </div>
                {activeDropdown === 'destination' && destSuggestions.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '6px', backgroundColor: isDark ? '#1f2937' : 'white', borderRadius: '10px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)', border: isDark ? '1px solid #374151' : '1px solid #e2e8f0', zIndex: 50, overflow: 'hidden' }}>
                    {destSuggestions.map((place, i) => (
                      <div key={i} onClick={() => selectPlace(place, 'destination')} style={{ padding: '10px 14px', fontSize: '13px', color: isDark ? '#d1d5db' : '#334155', cursor: 'pointer', borderBottom: i === destSuggestions.length -1 ? 'none' : (isDark ? '1px solid #374151' : '1px solid #f1f5f9') }} onMouseEnter={(e)=>e.currentTarget.style.backgroundColor=isDark ? '#374151' : '#f8fafc'} onMouseLeave={(e)=>e.currentTarget.style.backgroundColor='transparent'}>
                        <MapPin size={13} color="#94a3b8" style={{ display: 'inline', marginRight: '6px' }} /> {place}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Start Date */}
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: isDark ? '#9ca3af' : '#64748b', marginBottom: '6px', textTransform: 'uppercase' }}>Start Date</label>
                <input type="date" name="startDate" required value={formData.startDate} onChange={(e)=>setFormData({...formData, startDate: e.target.value})} style={{ width: '100%', boxSizing:'border-box', height: '44px', padding: '0 12px', borderRadius: '10px', border: isDark ? '1px solid #4b5563' : '1px solid #e2e8f0', outline: 'none', color: isDark ? 'white' : '#334151', fontSize: '13px', backgroundColor: isDark ? '#111827' : 'white' }} />
              </div>
              
              {/* End Date */}
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: isDark ? '#9ca3af' : '#64748b', marginBottom: '6px', textTransform: 'uppercase' }}>End Date</label>
                <input type="date" name="endDate" min={formData.startDate} required value={formData.endDate} onChange={(e)=>setFormData({...formData, endDate: e.target.value})} style={{ width: '100%', boxSizing:'border-box', height: '44px', padding: '0 12px', borderRadius: '10px', border: isDark ? '1px solid #4b5563' : '1px solid #e2e8f0', outline: 'none', color: isDark ? 'white' : '#334151', fontSize: '13px', backgroundColor: isDark ? '#111827' : 'white' }} />
              </div>
              
              {/* Adults Input */}
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: isDark ? '#9ca3af' : '#64748b', marginBottom: '6px', textTransform: 'uppercase' }}>Adults</label>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 10px', borderRadius: '10px', border: isDark ? '1px solid #4b5563' : '1px solid #e2e8f0', backgroundColor: isDark ? '#111827' : 'white', height: '44px', boxSizing: 'border-box' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Users size={15} color="#94a3b8" />
                    <span style={{ fontWeight: '600', color: isDark ? 'white' : '#334155', fontSize: '13px' }}>{formData.pax}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <button type="button" onClick={() => setFormData(p => ({...p, pax: Math.max(1, p.pax-1)}))} style={{ border:'none', background: formData.pax <= 1 ? (isDark ? '#374151' : '#f8fafc') : (isDark ? '#374151' : '#f1f5f9'), width:'26px', height:'26px', borderRadius:'50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: formData.pax <= 1 ? 'not-allowed' : 'pointer', color: formData.pax <= 1 ? '#4b5563' : '#64748b' }}><Minus size={13}/></button>
                    <button type="button" onClick={() => setFormData(p => ({...p, pax: p.pax+1}))} style={{ border:'none', background: isDark ? '#064e3b' : '#f0fdf4', width:'26px', height:'26px', borderRadius:'50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor:'pointer', color: '#16a34a' }}><Plus size={13}/></button>
                  </div>
                </div>
              </div>

              {/* Children Input */}
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: isDark ? '#9ca3af' : '#64748b', marginBottom: '6px', textTransform: 'uppercase' }}>Children</label>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 10px', borderRadius: '10px', border: isDark ? '1px solid #4b5563' : '1px solid #e2e8f0', backgroundColor: isDark ? '#111827' : 'white', height: '44px', boxSizing: 'border-box' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Users size={15} color="#94a3b8" />
                    <span style={{ fontWeight: '600', color: isDark ? 'white' : '#334155', fontSize: '13px' }}>{formData.children}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <button type="button" onClick={() => setFormData(p => ({...p, children: Math.max(0, p.children-1)}))} style={{ border:'none', background: formData.children <= 0 ? (isDark ? '#374151' : '#f8fafc') : (isDark ? '#374151' : '#f1f5f9'), width:'26px', height:'26px', borderRadius:'50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: formData.children <= 0 ? 'not-allowed' : 'pointer', color: formData.children <= 0 ? '#4b5563' : '#64748b' }}><Minus size={13}/></button>
                    <button type="button" onClick={() => setFormData(p => ({...p, children: p.children+1}))} style={{ border:'none', background: isDark ? '#064e3b' : '#f0fdf4', width:'26px', height:'26px', borderRadius:'50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor:'pointer', color: '#16a34a' }}><Plus size={13}/></button>
                  </div>
                </div>
              </div>

            </div>

            <label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: isDark ? '#9ca3af' : '#64748b', marginBottom: '6px', textTransform: 'uppercase' }}>Custom Requests / Interests</label>
            <textarea rows="3" value={formData.interests} onChange={(e)=>setFormData({...formData, interests: e.target.value})} placeholder="Tell us if you want to change hotels, add extra days, or have specific interests..." style={{ width: '100%', boxSizing:'border-box', padding: '12px', borderRadius: '10px', border: isDark ? '1px solid #4b5563' : '1px solid #e2e8f0', outline: 'none', marginBottom: '16px', fontFamily: 'inherit', color: isDark ? 'white' : '#334151', backgroundColor: isDark ? '#111827' : 'white', fontSize: '13px' }} />

            <div style={{ marginBottom: '25px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: isDark ? '#9ca3af' : '#64748b', marginBottom: '8px', fontWeight: '600' }}>
                <Tag size={13} /> Quick Select:
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {["🏔️ Mountains", "🏖️ Beaches", "🏛️ Heritage", "🍛 Local Food", "🐅 Safari", "🧘‍♀️ Wellness", "📸 Photography", "🎒 Trekking"].map((tag, idx) => {
                  const isActive = formData.interests.includes(tag);
                  return (
                    <button type="button" key={idx} onClick={() => handleTagClick(tag)} style={{ padding: '6px 12px', borderRadius: '50px', fontSize: '12px', cursor: 'pointer', transition: 'all 0.2s', fontWeight: '600', border: isActive ? '1px solid #16a34a' : (isDark ? '1px solid #4b5563' : '1px solid #e2e8f0'), backgroundColor: isActive ? (isDark ? '#064e3b' : '#f0fdf4') : (isDark ? '#111827' : '#f8fafc'), color: isActive ? (isDark ? '#a7f3d0' : '#16a34a') : (isDark ? '#9ca3af' : '#64748b') }}>
                      {tag}
                    </button>
                  );
                })}
              </div>
            </div>

            <button type="submit" style={{ width: '100%', padding: '15px', borderRadius: '12px', backgroundColor: '#16a34a', color: 'white', fontSize: '16px', fontWeight: 'bold', border: 'none', cursor: 'pointer', boxShadow: '0 10px 20px rgba(22,163,74,0.2)' }}>
              {selectedPlans.length > 0 || cartItems.length > 0 || routeStops.length > 0 ? `Confirm & Request Customization` : "Build My Itinerary"}
            </button>
            
          </form>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px', boxSizing: 'border-box' }}>
        
        {/* 3. DYNAMIC AI SEASONAL SUGGESTIONS */}
        <div style={{ marginBottom: '50px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
            <div style={{ padding: '8px', backgroundColor: isDark ? '#082f49' : '#e0f2fe', borderRadius: '10px' }}><Bot size={22} color={isDark ? '#38bdf8' : '#0284c7'} /></div>
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#111827', margin: 0 }}>Top Picks for {currentMonthNameDisplay}</h2>
          </div>
          
          {isLoadingSeasonal ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '30px', backgroundColor: isDark ? '#1f2937' : 'white', borderRadius: '16px', border: isDark ? '1px dashed #4b5563' : '1px dashed #cbd5e1' }}>
              <Loader2 size={26} color="#0284c7" className="animate-spin" style={{ marginBottom: '12px' }} />
              <p style={{ color: isDark ? '#9ca3af' : '#475569', fontWeight: '500', fontSize: '14px' }}>Our Team is finding the perfect weather spots for {currentMonthNameDisplay}...</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px' }}>
              {seasonalPicks.map((pick, idx) => (
                <div 
                  key={idx} onClick={() => handleTemplateConfirm({ title: pick.name, dest: pick.name, days: "Custom Duration", highlights: pick.desc, description: "Seasonal recommendation." })}
                  style={{ backgroundColor: isDark ? '#1f2937' : 'white', borderRadius: '18px', overflow: 'hidden', border: isDark ? '1px solid #374151' : '1px solid #e2e8f0', cursor: 'pointer', transition: 'all 0.3s ease', boxShadow: isDark ? '0 4px 10px rgba(0,0,0,0.3)' : '0 4px 10px rgba(0,0,0,0.03)', boxSizing: 'border-box' }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-6px)'; e.currentTarget.style.boxShadow = isDark ? '0 15px 30px rgba(0,0,0,0.6)' : '0 15px 30px rgba(0,0,0,0.08)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = isDark ? '0 4px 10px rgba(0,0,0,0.3)' : '0 4px 10px rgba(0,0,0,0.03)'; }}
                >
                  <div style={{ height: '170px', overflow: 'hidden', position: 'relative', backgroundColor: isDark ? '#374151' : '#e5e7eb' }}>
                    <img 
                      src={pick.img} 
                      alt={pick.name} 
                      onError={(e) => { e.target.src = '/logo.png'; }}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.5s' }} 
                      onMouseEnter={(e)=>e.currentTarget.style.transform='scale(1.05)'} 
                      onMouseLeave={(e)=>e.currentTarget.style.transform='scale(1)'}
                    />
                    <div style={{ position: 'absolute', top: '12px', right: '12px', backgroundColor: 'rgba(2, 132, 199, 0.9)', color: 'white', padding: '3px 8px', borderRadius: '50px', fontSize: '10px', fontWeight: 'bold' }}>Odessey's Pick</div>
                  </div>
                  <div style={{ padding: '20px', boxSizing: 'border-box' }}>
                    <h4 style={{ fontSize: '18px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#1e293b', marginBottom: '6px' }}>{pick.name}</h4>
                    <p style={{ fontSize: '13px', color: isDark ? '#d1d5db' : '#64748b', lineHeight: '1.5', margin: 0 }}>{pick.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 4. DYNAMIC ITINERARIES */}
        <div style={{ marginBottom: '50px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
             <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#111827', margin: 0 }}>
               {searchTarget ? `Itineraries for ${formData.destination}` : "Ready-to-Go Itineraries"}
             </h2>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
            {filteredItineraries.length > 0 && filteredItineraries.map((itin, idx) => (
              <div key={idx} style={{ backgroundColor: isDark ? '#1f2937' : 'white', padding: '24px', borderRadius: '18px', border: isDark ? '1px solid #374151' : '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
                <h4 style={{ fontSize: '18px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#111827', margin: '0 0 8px 0' }}>{itin.title}</h4>
                <p style={{ fontSize: '13px', color: isDark ? '#d1d5db' : '#64748b', marginBottom: '12px', lineHeight: '1.5' }}>{itin.description || itin.overview}</p>
                <div style={{ display: 'flex', gap: '12px', fontSize: '12px', color: isDark ? '#9ca3af' : '#475569', marginBottom: '16px', fontWeight: '600' }}>
                  <span><Clock size={13} style={{display:'inline', marginRight:'3px', verticalAlign:'text-bottom', color: '#94a3b8'}}/> {itin.days}</span>
                  <span><MapPin size={13} style={{display:'inline', marginRight:'3px', verticalAlign:'text-bottom', color: '#94a3b8'}}/> {(itin.dest || itin.location || '').split(',')[0]}</span>
                </div>
                <button 
                  onClick={() => { setPreviewItin(itin); setModalState('previewItin'); }} 
                  style={{ marginTop: 'auto', padding: '10px', borderRadius: '10px', backgroundColor: 'transparent', color: isDark ? '#38bdf8' : '#0284c7', border: isDark ? '1px solid #38bdf8' : '1px solid #0284c7', fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.2s', fontSize: '13px' }} 
                  onMouseEnter={(e) => {e.currentTarget.style.backgroundColor = isDark ? '#082f49' : '#e0f2fe'}} 
                  onMouseLeave={(e) => {e.currentTarget.style.backgroundColor = 'transparent'}}
                >
                  View Details
                </button>
              </div>
            ))}

            {isGeneratingAI && (
              <div style={{ gridColumn: '1 / -1', maxWidth: '600px', margin: '0 auto', width: '100%', backgroundColor: isDark ? '#064e3b' : '#f0fdf4', border: isDark ? '1px dashed #065f46' : '1px dashed #16a34a', borderRadius: '18px', padding: '25px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', boxSizing: 'border-box' }}>
                <Loader2 size={26} color="#16a34a" className="animate-spin" style={{ marginBottom: '12px' }} />
                <h4 style={{ fontSize: '16px', fontWeight: 'bold', color: isDark ? '#a7f3d0' : '#15803d', margin: '0 0 6px 0' }}>Mapping Route...</h4>
                <p style={{ fontSize: '13px', color: isDark ? '#d1fae5' : '#166534', margin: 0 }}>Crafting an optimal {currentCalcDuration} route from {formData.origin || 'Origin'} to {formData.destination} with intermediate stops.</p>
              </div>
            )}

            {!isGeneratingAI && aiGeneratedItinerary && (
              <div style={{ gridColumn: '1 / -1', maxWidth: '600px', margin: '0 auto', width: '100%', backgroundColor: isDark ? '#1f2937' : '#f8fafc', padding: '30px 20px', borderRadius: '18px', border: isDark ? '1px solid #0284c7' : '1px solid #bae6fd', display: 'flex', flexDirection: 'column', position: 'relative', alignItems: 'center', textAlign: 'center', boxSizing: 'border-box' }}>
                <div style={{ position: 'absolute', top: '-10px', left: '50%', transform: 'translateX(-50%)', backgroundColor: isDark ? '#082f49' : '#e0f2fe', color: isDark ? '#38bdf8' : '#0284c7', padding: '3px 14px', borderRadius: '50px', fontSize: '11px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap' }}>
                  <Bot size={13} /> Custom Route
                </div>
                <h4 style={{ fontSize: '20px', fontWeight: 'bold', margin: '0 0 10px 0', color: isDark ? '#f9fafb' : '#111827' }}>{aiGeneratedItinerary.title}</h4>
                <p style={{ fontSize: '13px', color: isDark ? '#9ca3af' : '#64748b', marginBottom: '16px', lineHeight: '1.5' }}>{aiGeneratedItinerary.description}</p>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', fontSize: '13px', color: isDark ? '#d1d5db' : '#475569', marginBottom: '20px', fontWeight: '600', flexWrap: 'wrap' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Clock size={15} color="#94a3b8"/> {aiGeneratedItinerary.days}</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><MapPin size={15} color="#94a3b8"/> {aiGeneratedItinerary.dest}</span>
                </div>
                <button 
                  onClick={() => { setPreviewItin(aiGeneratedItinerary); setModalState('previewItin'); }} 
                  style={{ padding: '12px', borderRadius: '10px', backgroundColor: '#0284c7', color: 'white', border: 'none', fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.2s', width: '100%', fontSize: '14px' }} 
                  onMouseEnter={(e) => {e.currentTarget.style.backgroundColor = '#0369a1'}} 
                  onMouseLeave={(e) => {e.currentTarget.style.backgroundColor = '#0284c7'}}
                >
                  View Full Itinerary
                </button>
              </div>
            )}
          </div>
        </div>

        {/* 5. CURATED LOCAL EXPERIENCES */}
        {filteredExperiences.length > 0 ? (
          <div style={{ marginBottom: '50px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
               <div style={{ padding: '8px', backgroundColor: isDark ? '#064e3b' : '#dcfce7', borderRadius: '10px' }}><Activity size={22} color="#16a34a" /></div>
               <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#111827', margin: 0 }}>Curated Local Experiences</h2>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
              {filteredExperiences.map((exp) => (
                <div 
                  key={exp.id} 
                  style={{ backgroundColor: isDark ? '#1f2937' : 'white', borderRadius: '20px', overflow: 'hidden', border: isDark ? '1px solid #374151' : '1px solid #e5e7eb', boxShadow: isDark ? '0 10px 30px rgba(0,0,0,0.5)' : '0 10px 25px rgba(0,0,0,0.03)', transition: 'transform 0.3s, background-color 0.3s, border-color 0.3s', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }} 
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-6px)'; }} 
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  <div style={{ height: '200px', position: 'relative', backgroundColor: isDark ? '#374151' : '#e5e7eb' }}>
                    <img src={exp.image} alt={exp.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <div style={{ position: 'absolute', top: '12px', left: '12px', backgroundColor: isDark ? 'rgba(31, 41, 55, 0.9)' : 'rgba(255,255,255,0.9)', backdropFilter: 'blur(4px)', color: isDark ? '#4ade80' : '#16a34a', padding: '5px 10px', borderRadius: '50px', fontSize: '11px', fontWeight: 'bold', display: 'flex', gap: '4px', alignItems: 'center' }}>
                      <Sparkles size={13} /> {exp.category}
                    </div>
                  </div>
                  <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', flex: 1, boxSizing: 'border-box' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: isDark ? '#9ca3af' : '#6b7280', fontSize: '12px', fontWeight: '600' }}>
                        <MapPin size={13} /> {exp.location}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: isDark ? '#d1d5db' : '#4b5563', fontSize: '12px', fontWeight: '600', backgroundColor: isDark ? '#374151' : '#f3f4f6', padding: '3px 7px', borderRadius: '6px' }}>
                        <Clock size={11} /> {exp.duration}
                      </div>
                    </div>
                    <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#111827', marginBottom: '10px', lineHeight: '1.3' }}>{exp.title}</h3>
                    <p style={{ fontSize: '13px', color: isDark ? '#d1d5db' : '#4b5563', lineHeight: '1.5', marginBottom: '20px', flex: 1 }}>{exp.desc}</p>
                    
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button onClick={() => { setPreviewExp(exp); setModalState('previewExp'); }} style={{ flex: 1, padding: '10px', borderRadius: '10px', backgroundColor: 'transparent', color: isDark ? '#4ade80' : '#16a34a', border: isDark ? '2px solid #4ade80' : '2px solid #16a34a', fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.2s', fontSize: '13px' }} onMouseEnter={(e)=>{e.currentTarget.style.backgroundColor=isDark ? '#4ade80' : '#16a34a'; e.currentTarget.style.color=isDark ? '#030712' : 'white';}} onMouseLeave={(e)=>{e.currentTarget.style.backgroundColor='transparent'; e.currentTarget.style.color=isDark ? '#4ade80' : '#16a34a';}}>
                        Details
                      </button>
                      <button onClick={() => handleExperienceConfirm(exp)} style={{ flex: 1, padding: '10px', borderRadius: '10px', backgroundColor: '#16a34a', color: 'white', border: 'none', fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.2s', fontSize: '13px' }} onMouseEnter={(e)=>e.currentTarget.style.backgroundColor='#15803d'} onMouseLeave={(e)=>e.currentTarget.style.backgroundColor='#16a34a'}>
                        Add to Plan
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : searchTarget ? (
          <div style={{ marginBottom: '50px', padding: '30px 20px', backgroundColor: isDark ? '#1f2937' : '#f8fafc', borderRadius: '18px', textAlign: 'center', border: isDark ? '1px dashed #4b5563' : '1px dashed #cbd5e1', boxSizing: 'border-box' }}>
            <Activity size={26} color="#94a3b8" style={{ margin: '0 auto 10px' }} />
            <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#334155' }}>No curated experiences loaded for {formData.destination} yet.</h3>
            <p style={{ fontSize: '13px', color: isDark ? '#9ca3af' : '#64748b' }}>Don't worry! If you submit your plan, our experts will build custom activities for you.</p>
          </div>
        ) : null}
      </div>

      {/* --- OVERLAY MODALS --- */}
      {modalState !== 'none' && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 100, backgroundColor: 'rgba(15, 23, 42, 0.8)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', boxSizing: 'border-box' }}>
          
          {/* 1. ITINERARY PREVIEW MODAL */}
          {modalState === 'previewItin' && previewItin && (
            <div style={{ margin: 'auto', backgroundColor: isDark ? '#1f2937' : 'white', width: '100%', maxWidth: '650px', borderRadius: '20px', padding: '30px 20px', position: 'relative', boxShadow: '0 25px 50px rgba(0,0,0,0.5)', animation: 'slideUp 0.3s ease-out forwards', maxHeight: '90vh', overflowY: 'auto', border: isDark ? '1px solid #374151' : 'none', boxSizing: 'border-box' }} className="hide-scrollbar">
              <button onClick={closeModal} style={{ position: 'absolute', top: '15px', right: '15px', background: isDark ? '#374151' : '#f1f5f9', border: 'none', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: isDark ? 'white' : '#64748b' }}><X size={18} /></button>
              
              <div style={{ backgroundColor: previewItin.isAiGenerated ? (isDark ? '#082f49' : '#e0f2fe') : (isDark ? '#064e3b' : '#f0fdf4'), color: previewItin.isAiGenerated ? (isDark ? '#38bdf8' : '#0284c7') : (isDark ? '#a7f3d0' : '#16a34a'), display: 'inline-block', padding: '5px 12px', borderRadius: '50px', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '12px' }}>
                {previewItin.isAiGenerated ? "✨Generated Route" : "Itinerary Preview"}
              </div>
              <h2 style={{ fontSize: 'clamp(22px, 4vw, 26px)', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#1e293b', margin: '0 0 8px 0' }}>{previewItin.title}</h2>
              <div style={{ display: 'flex', gap: '16px', fontSize: '13px', color: isDark ? '#9ca3af' : '#64748b', marginBottom: '20px', fontWeight: '600', paddingBottom: '16px', borderBottom: isDark ? '1px solid #374151' : '1px solid #e2e8f0', flexWrap: 'wrap' }}>
                <span><Clock size={15} style={{display:'inline', marginRight:'5px', verticalAlign:'text-bottom'}}/> {previewItin.days}</span>
                <span><MapPin size={15} style={{display:'inline', marginRight:'5px', verticalAlign:'text-bottom'}}/> {previewItin.dest || previewItin.location}</span>
              </div>
              
              <h4 style={{ fontSize: '15px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#334155', marginBottom: '8px' }}>Overview</h4>
              <p style={{ fontSize: '14px', color: isDark ? '#d1d5db' : '#475569', lineHeight: '1.6', marginBottom: '20px' }}>{previewItin.description || previewItin.overview}</p>
              
              <h4 style={{ fontSize: '15px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#334155', marginBottom: '8px' }}>Key Highlights</h4>
              <ul style={{ paddingLeft: '18px', color: isDark ? '#d1d5db' : '#475569', fontSize: '14px', lineHeight: '1.6', marginBottom: '20px' }}>
                {(Array.isArray(previewItin.highlights) 
                  ? previewItin.highlights 
                  : (previewItin.highlights || "Customizable highlights based on your preferences.").split(', ')
                ).map((hl, i) => <li key={i} style={{ marginBottom: '6px' }}>{hl}</li>)}
              </ul>

              {previewItin.plan && previewItin.plan.length > 0 && (
                <>
                  <h4 style={{ fontSize: '15px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#334155', marginBottom: '12px' }}>Suggested Flow</h4>
                  <div style={{ borderLeft: isDark ? '2px solid #4b5563' : '2px solid #e2e8f0', marginLeft: '8px', paddingLeft: '16px', marginBottom: '25px' }}>
                    {previewItin.plan.map((day, idx) => (
                      <div key={idx} style={{ position: 'relative', marginBottom: '14px' }}>
                        <div style={{ position: 'absolute', left: '-23px', top: '4px', width: '9px', height: '9px', borderRadius: '50%', backgroundColor: previewItin.isAiGenerated ? '#0284c7' : '#16a34a' }} />
                        <h5 style={{ fontSize: '13px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#1e293b', margin: '0 0 3px 0' }}>Day {day.day}: {day.title}</h5>
                        <p style={{ fontSize: '13px', color: isDark ? '#9ca3af' : '#64748b', margin: 0, lineHeight: '1.5' }}>{day.desc}</p>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <button onClick={() => handleTemplateConfirm(previewItin)} style={{ width: '100%', padding: '14px', borderRadius: '10px', backgroundColor: previewItin.isAiGenerated ? '#0284c7' : '#16a34a', color: 'white', fontSize: '15px', fontWeight: 'bold', border: 'none', cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0, 0.15)' }}>
                Add this to my Planner
              </button>
            </div>
          )}

          {/* 2. EXPERIENCE PREVIEW MODAL */}
          {modalState === 'previewExp' && previewExp && (
            <div style={{ margin: 'auto', backgroundColor: isDark ? '#1f2937' : 'white', width: '100%', maxWidth: '650px', borderRadius: '20px', padding: '30px 20px', position: 'relative', boxShadow: '0 25px 50px rgba(0,0,0,0.5)', animation: 'slideUp 0.3s ease-out forwards', maxHeight: '90vh', overflowY: 'auto', border: isDark ? '1px solid #374151' : 'none', boxSizing: 'border-box' }} className="hide-scrollbar">
              <button onClick={closeModal} style={{ position: 'absolute', top: '15px', right: '15px', background: isDark ? '#374151' : '#f1f5f9', border: 'none', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: isDark ? 'white' : '#64748b' }}><X size={18} /></button>
              
              <div style={{ backgroundColor: isDark ? '#064e3b' : '#f0fdf4', color: isDark ? '#a7f3d0' : '#16a34a', display: 'inline-block', padding: '5px 12px', borderRadius: '50px', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '12px' }}>Experience Details</div>
              <h2 style={{ fontSize: 'clamp(22px, 4vw, 26px)', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#1e293b', margin: '0 0 8px 0' }}>{previewExp.title}</h2>
              <div style={{ display: 'flex', gap: '16px', fontSize: '13px', color: isDark ? '#9ca3af' : '#64748b', marginBottom: '20px', fontWeight: '600', paddingBottom: '16px', borderBottom: isDark ? '1px solid #374151' : '1px solid #e2e8f0', flexWrap: 'wrap' }}>
                <span><Clock size={15} style={{display:'inline', marginRight:'5px', verticalAlign:'text-bottom'}}/> {previewExp.duration}</span>
                <span><MapPin size={15} style={{display:'inline', marginRight:'5px', verticalAlign:'text-bottom'}}/> {previewExp.location}</span>
              </div>
              
              <h4 style={{ fontSize: '16px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#111827', marginBottom: '8px' }}>What to Expect</h4>
              <p style={{ fontSize: '14px', color: isDark ? '#d1d5db' : '#4b5563', lineHeight: '1.7', marginBottom: '25px' }}>{previewExp.fullDesc}</p>
              
              <h4 style={{ fontSize: '16px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#111827', marginBottom: '12px' }}>What's Included</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px', marginBottom: '25px' }}>
                {previewExp.inclusions.map((item, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', backgroundColor: isDark ? '#111827' : '#f9fafb', padding: '10px 12px', borderRadius: '10px', border: isDark ? '1px solid #374151' : '1px solid #e5e7eb', boxSizing: 'border-box' }}>
                    <CheckCircle2 size={16} color="#16a34a" style={{ flexShrink: 0, marginTop: '2px' }} />
                    <span style={{ color: isDark ? '#d1d5db' : '#4b5563', fontSize: '13px', fontWeight: '600' }}>{item}</span>
                  </div>
                ))}
              </div>

              <button onClick={() => handleExperienceConfirm(previewExp)} style={{ width: '100%', padding: '14px', borderRadius: '10px', backgroundColor: '#16a34a', color: 'white', fontSize: '15px', fontWeight: 'bold', border: 'none', cursor: 'pointer', boxShadow: '0 4px 12px rgba(22, 163, 74, 0.2)' }}>
                Add this to my Planner
              </button>
            </div>
          )}

          {/* 3. SUCCESS MODAL */}
          {modalState === 'success' && (
            <div style={{ margin: 'auto', backgroundColor: isDark ? '#1f2937' : 'white', padding: '35px 20px', borderRadius: '20px', textAlign: 'center', maxWidth: '380px', border: isDark ? '1px solid #374151' : 'none', boxSizing: 'border-box' }}>
              <CheckCircle size={50} color="#16a34a" style={{ margin: '0 auto 16px' }} />
              <h2 style={{ color: isDark ? '#f9fafb' : '#111827', fontSize: '20px', marginBottom: '8px' }}>Request Prepared!</h2>
              <p style={{ color: isDark ? '#d1d5db' : '#64748b', fontSize: '13px', lineHeight: '1.5' }}>Your WhatsApp or Email should have opened automatically to send your request to our travel team!</p>
              <button onClick={closeModal} style={{ padding: '10px 24px', borderRadius: '50px', backgroundColor: '#16a34a', color: 'white', border: 'none', cursor: 'pointer', marginTop: '16px', fontWeight: 'bold', fontSize: '14px' }}>Done</button>
            </div>
          )}

          {/* 4. CONTACT MODAL */}
          {modalState === 'contact' && (
            <div style={{ margin: 'auto', backgroundColor: isDark ? '#1f2937' : 'white', width: '100%', maxWidth: '450px', borderRadius: '20px', padding: '30px 20px', position: 'relative', boxShadow: '0 25px 50px rgba(0,0,0,0.5)', animation: 'slideUp 0.3s ease-out forwards', border: isDark ? '1px solid #374151' : 'none', boxSizing: 'border-box' }}>
              <button onClick={closeModal} style={{ position: 'absolute', top: '15px', right: '15px', background: isDark ? '#374151' : '#f1f5f9', border: 'none', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: isDark ? 'white' : '#64748b' }}><X size={18} /></button>
              
              <form onSubmit={handleSubmitRequest}>
                <div style={{ width: '50px', height: '50px', borderRadius: '50%', backgroundColor: isDark ? '#082f49' : '#e0f2fe', color: isDark ? '#38bdf8' : '#0284c7', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}><Map size={24} /></div>
                <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#1e293b', marginBottom: '8px' }}>Where should we send your request?</h2>
                <p style={{ color: isDark ? '#9ca3af' : '#64748b', marginBottom: '20px', fontSize: '13px', lineHeight: '1.5' }}>Our travel experts are reviewing your request for <strong>{formData.destination || 'your destination'}</strong>. Choose how you'd like to receive the details.</p>

                <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
                  <button type="button" onClick={() => { setContactMethod('whatsapp'); setContactValue(''); }} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: contactMethod === 'whatsapp' ? '2px solid #25D366' : (isDark ? '1px solid #4b5563' : '1px solid #e2e8f0'), backgroundColor: contactMethod === 'whatsapp' ? (isDark ? '#064e3b' : '#f0fdf4') : (isDark ? '#111827' : 'white'), color: contactMethod === 'whatsapp' ? '#16a348' : (isDark ? '#9ca3af' : '#64748b'), fontWeight: 'bold', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', cursor: 'pointer' }}><MessageCircle size={16} /> WhatsApp</button>
                  <button type="button" onClick={() => { setContactMethod('email'); setContactValue(''); }} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: contactMethod === 'email' ? '2px solid #16a34a' : (isDark ? '1px solid #4b5563' : '1px solid #e2e8f0'), backgroundColor: contactMethod === 'email' ? (isDark ? '#064e3b' : '#f0fdf4') : (isDark ? '#111827' : 'white'), color: contactMethod === 'email' ? '#15803d' : (isDark ? '#9ca3af' : '#64748b'), fontWeight: 'bold', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', cursor: 'pointer' }}><Mail size={16} /> Email</button>
                </div>

                <input 
                  type={contactMethod === 'email' ? 'email' : 'tel'} 
                  placeholder={contactMethod === 'email' ? 'Your email address' : 'Your WhatsApp number (e.g. 9876543210)'}
                  value={contactValue} onChange={(e) => setContactValue(e.target.value)} required
                  style={{ width: '100%', boxSizing: 'border-box', padding: '14px', borderRadius: '10px', border: isDark ? '1px solid #4b5563' : '1px solid #e2e8f0', fontSize: '14px', outline: 'none', marginBottom: '20px', backgroundColor: isDark ? '#111827' : 'white', color: isDark ? 'white' : '#111827' }}
                />
                <button type="submit" style={{ width: '100%', padding: '14px', borderRadius: '10px', backgroundColor: '#16a34a', color: 'white', fontSize: '15px', fontWeight: 'bold', border: 'none', cursor: 'pointer' }}>Send Request via {contactMethod === 'whatsapp' ? 'WhatsApp' : 'Email'}</button>
              </form>
            </div>
          )}
        </div>
      )}

      <style>{`
        .form-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 16px;
          margin-bottom: 20px;
        }
        
        @media (min-width: 640px) {
          .form-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        
        @media (min-width: 1024px) {
          .form-grid {
            grid-template-columns: repeat(4, 1fr);
          }
          .col-span-2 {
            grid-column: span 2;
          }
        }

        .animate-spin { animation: spin 1s linear infinite; }
        @keyframes spin { 100% { transform: rotate(360deg); } }
        @keyframes slideDown { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

export default PlanTrip;